/* eslint-disable react/require-default-props */
import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { FaMinus, FaPlus } from 'react-icons/fa';

import { INTEGER_RE } from './constants';

// ---------------------------------------------------------------------------
// Stepper de quantidade (− | campo | +) do redesenho das telas de movimento
// (2026-09-12). Dois portes: `md` na linha de adição (40px) e `sm` dentro da
// tabela de itens (28px), como no protótipo.
//
// O campo aceita string vazia ENQUANTO o foco está nele: apagar para digitar
// outro número é fluxo normal de operação. **No blur o valor é normalizado
// para o mínimo** (2026-09-12) — sair do campo vazio não deixa mais a lista
// num estado inválido, então nenhuma tela precisa tratar "quantidade em
// branco" como pendência de formulário.
//
// ⚠️ Os botões declaram o PRÓPRIO `:hover` com `background` (armadilha G.4.1):
// `button:hover` do GlobalStyles é 0,0,1,1 e venceria a classe do styled.
// ---------------------------------------------------------------------------

const SIZES = {
   md: { control: 40, field: 72, font: 14, icon: 11 },
   sm: { control: 28, field: 52, font: 13, icon: 9 },
};

const Wrap = styled.div`
   display: inline-flex;
   align-items: center;
   gap: 6px;
`;

const StepButton = styled.button`
   display: inline-flex;
   align-items: center;
   justify-content: center;
   flex-shrink: 0;
   width: ${({ $size }) => $size}px;
   height: ${({ $size }) => $size}px;
   padding: 0;
   border: 1px solid ${({ theme }) => theme.colors.border};
   border-radius: ${({ $size }) => ($size >= 40 ? 10 : 8)}px;
   background: ${({ theme }) => theme.colors.surface};
   color: ${({ theme }) => theme.colors.textMuted};
   cursor: pointer;
   transition:
      background 0.13s ease,
      color 0.13s ease;

   &:hover:not(:disabled) {
      background: rgba(24, 95, 165, 0.08);
      color: ${({ theme }) => theme.colors.link};
   }
   &:disabled {
      cursor: not-allowed;
      opacity: 0.55;
   }
   &:focus-visible {
      outline: none;
      box-shadow: 0 0 0 3px ${({ theme }) => theme.colors.focusRing};
   }
`;

const QtyField = styled.input`
   && {
      width: ${({ $width }) => $width}px;
      min-width: 0;
      height: ${({ $height }) => $height}px;
      min-height: 0;
      padding: 0 6px;
      border-radius: ${({ $height }) => ($height >= 40 ? 10 : 8)}px;
      border: 1px solid
         ${({ $invalid, theme }) =>
            $invalid ? theme.colors.danger : theme.colors.border};
      background: ${({ theme }) => theme.colors.surface};
      color: ${({ theme }) => theme.colors.textPrimary};
      font-size: ${({ $font }) => $font}px;
      font-weight: 700;
      font-variant-numeric: tabular-nums;
      text-align: center;
   }
`;

export default function QuantityStepper({
   value,
   onChange,
   size = 'md',
   disabled = false,
   invalid = false,
   min = 1,
   ariaLabel = 'Quantidade',
   onKeyDown,
   onBlur,
   inputRef,
}) {
   const dim = SIZES[size] ?? SIZES.md;
   const current = Number(value);
   const safeCurrent = Number.isFinite(current) ? current : 0;

   const step = (delta) => {
      const next = Math.max(min, safeCurrent + delta);
      onChange(String(next));
   };

   const handleChange = (event) => {
      const next = event.target.value;
      if (next === '' || INTEGER_RE.test(next)) onChange(next);
   };

   // Campo vazio (ou abaixo do mínimo) só existe durante a digitação: ao sair,
   // volta para o mínimo. É o que garante que item na lista sempre tem
   // quantidade válida — a validação vive no controle, não num checklist que o
   // operador precisa ler.
   const handleBlur = (event) => {
      const raw = String(value ?? '').trim();
      const parsed = Number(raw);
      if (raw === '' || !Number.isFinite(parsed) || parsed < min) {
         onChange(String(min));
      }
      if (typeof onBlur === 'function') onBlur(event);
   };

   return (
      <Wrap>
         <StepButton
            type="button"
            $size={dim.control}
            onClick={() => step(-1)}
            disabled={disabled || safeCurrent <= min}
            title="Diminuir"
            aria-label="Diminuir quantidade"
         >
            <FaMinus size={dim.icon} aria-hidden="true" />
         </StepButton>

         <QtyField
            type="text"
            inputMode="numeric"
            ref={inputRef}
            value={value}
            disabled={disabled}
            onChange={handleChange}
            onBlur={handleBlur}
            onKeyDown={onKeyDown}
            placeholder="0"
            aria-label={ariaLabel}
            aria-invalid={invalid || undefined}
            $invalid={invalid}
            $width={dim.field}
            $height={dim.control}
            $font={dim.font}
         />

         <StepButton
            type="button"
            $size={dim.control}
            onClick={() => step(1)}
            disabled={disabled}
            title="Aumentar"
            aria-label="Aumentar quantidade"
         >
            <FaPlus size={dim.icon} aria-hidden="true" />
         </StepButton>
      </Wrap>
   );
}

QuantityStepper.propTypes = {
   value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
   onChange: PropTypes.func.isRequired,
   size: PropTypes.oneOf(['md', 'sm']),
   disabled: PropTypes.bool,
   invalid: PropTypes.bool,
   min: PropTypes.number,
   ariaLabel: PropTypes.string,
   onKeyDown: PropTypes.func,
   // Chamado DEPOIS da normalização do valor.
   onBlur: PropTypes.func,
   inputRef: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
};
